import type {
  DeploymentEnvironment,
  DeploymentRollbackPreflightResponse,
  DeploymentRollbackResponse,
  PrismaRollbackMigration,
} from '@dev-console/types';
import { pluralize } from '@shared/strings/utils';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { preflightDeploymentRollback, rollbackDeployment } from '@/config/api-helpers';

interface RollbackDialogProps {
  environment: DeploymentEnvironment;
  initialCommitSha?: string;
  /** Runtime SHA the console currently displays; the workflow fails closed if the runtime moves before it runs. */
  deployedCommitSha?: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (err: unknown) => void;
}

type DialogState = 'form' | 'preflight-loading' | 'review' | 'confirm' | 'dispatch-loading' | 'success';

function PrismaMigrationPreview({ migration }: { migration: PrismaRollbackMigration }) {
  const isReversible = migration.classification === 'reversible';

  return (
    <details
      className={`rounded-md border ${isReversible ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
    >
      <summary
        className={`cursor-pointer px-3 py-2 font-mono text-xs ${isReversible ? 'text-green-950' : 'text-red-950'}`}
      >
        {migration.name} — {migration.classification}
      </summary>
      <div className={`border-t bg-white p-3 ${isReversible ? 'border-green-200' : 'border-red-200'}`}>
        {migration.downSql != null ? (
          <>
            <p className='mb-2 break-all font-mono text-xs text-neutral-600'>{migration.downSqlPath}</p>
            <pre className='max-h-64 overflow-auto whitespace-pre-wrap rounded bg-neutral-900 p-3 text-xs text-neutral-100'>
              {migration.downSql}
            </pre>
          </>
        ) : (
          <p className='text-xs text-neutral-600'>No executable down.sql is available.</p>
        )}
      </div>
    </details>
  );
}

export function RollbackDialog({
  environment,
  initialCommitSha = '',
  deployedCommitSha,
  onClose,
  onSuccess,
  onError,
}: RollbackDialogProps) {
  const [state, setState] = useState<DialogState>('form');
  const [commitSha, setCommitSha] = useState(initialCommitSha);
  const [jobServiceName, setJobServiceName] = useState('');
  const [preflight, setPreflight] = useState<DeploymentRollbackPreflightResponse | null>(null);
  const [rollbackDatabase, setRollbackDatabase] = useState(false);
  const [result, setResult] = useState<DeploymentRollbackResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const titleId = useId();

  const isProduction = environment === 'production';
  const trimmedSha = commitSha.trim();
  const shortSha = trimmedSha.slice(0, 7);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Don't dismiss mid-dispatch — request may still complete.
      if (state === 'preflight-loading' || state === 'dispatch-loading') return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, state]);

  const runPreflight = async () => {
    if (!/^[0-9a-f]{7,40}$/i.test(trimmedSha)) {
      setFormError('Enter a 7-40 character git SHA');
      return;
    }
    setFormError(null);

    if (jobServiceName.trim() !== '') {
      setRollbackDatabase(false);
      setState('confirm');
      return;
    }

    setState('preflight-loading');
    try {
      const response = await preflightDeploymentRollback({
        environment,
        commitSha: trimmedSha,
        ...(deployedCommitSha != null ? { deployedCommitSha } : {}),
      });
      setPreflight(response);
      setRollbackDatabase(response.databaseRollbackStatus === 'available');
      setState('review');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Unable to check Prisma rollback eligibility');
      setState('form');
    }
  };

  const handleContinue = () => {
    void runPreflight();
  };

  const handleDispatch = async () => {
    setState('dispatch-loading');
    try {
      const response = await rollbackDeployment({
        environment,
        commitSha: trimmedSha,
        ...(deployedCommitSha != null ? { deployedCommitSha } : {}),
        ...(jobServiceName.trim() ? { jobServiceName: jobServiceName.trim() } : {}),
        rollbackDatabase,
      });
      setResult(response);
      setState('success');
      onSuccess();
    } catch (err) {
      onError(err);
      onClose();
    }
  };

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4'
    >
      <div className='flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <h3 id={titleId} className='text-lg font-medium text-neutral-900'>
            {state === 'review'
              ? `Review ${environment} rollback`
              : state === 'confirm'
              ? `Confirm ${environment} rollback`
              : `Roll back ${environment} runtime`}
          </h3>
        </div>

        <div className='min-h-0 space-y-4 overflow-y-auto px-6 py-5'>
          {state === 'form' && (
            <>
              <p className='text-sm text-neutral-700'>
                This will dispatch <code className='rounded bg-neutral-100 px-1'>rollback-deploy.yml</code>. Prisma
                migration-boundary checks still run in Actions.
              </p>
              {isProduction && (
                <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900'>
                  You are targeting <span className='font-medium'>production</span>. Confirm the commit carefully before
                  dispatching.
                </div>
              )}
              <label className='block text-sm text-neutral-700'>
                Target commit SHA
                <input
                  className='mt-1 w-full rounded-md border border-neutral-200 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
                  placeholder='e.g. a1b2c3d'
                  value={commitSha}
                  onChange={(event) => {
                    setCommitSha(event.target.value);
                    setFormError(null);
                  }}
                />
              </label>
              <label className='block text-sm text-neutral-700'>
                Optional jobs service name
                <input
                  className='mt-1 w-full rounded-md border border-neutral-200 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
                  placeholder='e.g. healthCheck-staging'
                  value={jobServiceName}
                  onChange={(event) => setJobServiceName(event.target.value)}
                />
              </label>
              {formError != null && <p className='text-sm text-red-600'>{formError}</p>}
            </>
          )}

          {state === 'review' && preflight != null && (
            <>
              {preflight.databaseRollbackStatus === 'not-required' && (
                <div className='rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900'>
                  No Prisma migrations are crossed. This will be a code-only rollback.
                </div>
              )}
              {preflight.databaseRollbackStatus === 'available' && (
                <div className='rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900'>
                  This rollback crosses {preflight.counts.reversible} Prisma migration
                  {preflight.counts.reversible === 1 ? ', and it' : 's, and all'} can be reversed.
                </div>
              )}
              {preflight.databaseRollbackStatus === 'available' && (
                <fieldset className='space-y-2'>
                  <legend className='mb-2 text-sm font-medium text-neutral-900'>Rollback mode</legend>
                  <label className='flex cursor-pointer gap-3 rounded-md border border-neutral-200 p-3 text-sm'>
                    <input
                      type='radio'
                      name='rollback-mode'
                      checked={rollbackDatabase}
                      onChange={() => setRollbackDatabase(true)}
                    />
                    <span>
                      <span className='block font-medium text-neutral-900'>Code + database</span>
                      <span className='text-neutral-600'>Apply down migrations after application rollback.</span>
                    </span>
                  </label>
                  <label className='flex cursor-pointer gap-3 rounded-md border border-neutral-200 p-3 text-sm'>
                    <input
                      type='radio'
                      name='rollback-mode'
                      checked={!rollbackDatabase}
                      onChange={() => setRollbackDatabase(false)}
                    />
                    <span>
                      <span className='block font-medium text-neutral-900'>Code only</span>
                      <span className='text-neutral-600'>Leave all database migrations applied.</span>
                    </span>
                  </label>
                </fieldset>
              )}
              {preflight.databaseRollbackStatus === 'unavailable' && (
                <div className='rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950'>
                  <p className='font-medium'>Database rollback is unavailable for this range.</p>
                  <p className='mt-2'>
                    Cancel or proceed with a code-only rollback. All crossed migrations will remain applied.
                  </p>
                </div>
              )}
              {(rollbackDatabase || preflight.databaseRollbackStatus === 'unavailable') &&
                preflight.migrations.length > 0 && (
                  <div className='space-y-2'>
                    <p className='text-sm font-medium text-neutral-900'>
                      Crossed Prisma {pluralize(preflight.migrations.length, 'migration')}
                    </p>
                    {preflight.migrations.map((migration) => (
                      <PrismaMigrationPreview key={migration.name} migration={migration} />
                    ))}
                  </div>
                )}
            </>
          )}

          {state === 'confirm' && (
            <div
              className={`rounded-md border p-4 text-sm ${
                isProduction ? 'border-red-300 bg-red-50 text-red-950' : 'border-amber-200 bg-amber-50 text-amber-950'
              }`}
            >
              <p className='font-medium'>
                Dispatch rollback of {environment} runtime to{' '}
                <code className='rounded bg-white/70 px-1 font-mono'>{shortSha}</code>?
              </p>
              <p className='mt-2'>
                This starts the GitHub Actions rollback workflow, which disables {environment} deployments, cancels
                active deployment runs, and waits for them to stop before rolling back. Deployments remain disabled
                until manually re-enabled.
              </p>
              {deployedCommitSha != null && (
                <p className='mt-2 text-xs opacity-80'>
                  Last observed runtime:{' '}
                  <code className='rounded bg-white/70 px-1 font-mono'>{deployedCommitSha.slice(0, 7)}</code> — the
                  workflow checks the actual runtime after stopping deployments and uses it to validate the rollback.
                </p>
              )}
              {jobServiceName.trim() === '' && (
                <p className='mt-2 font-medium'>Mode: {rollbackDatabase ? 'Code + database' : 'Code only'}</p>
              )}
              {jobServiceName.trim() !== '' && (
                <p className='mt-2 text-xs opacity-80'>Jobs service: {jobServiceName.trim()}</p>
              )}
            </div>
          )}

          {state === 'preflight-loading' && (
            <p className='py-6 text-center text-sm text-neutral-500'>Checking Prisma rollback eligibility…</p>
          )}

          {state === 'dispatch-loading' && (
            <p className='py-6 text-center text-sm text-neutral-500'>Dispatching rollback workflow…</p>
          )}

          {state === 'success' && result != null && (
            <div className='rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800'>
              <p className='mb-2'>Rollback workflow dispatched for {result.commitSha.slice(0, 7)}.</p>
              <a href={result.workflowUrl} target='_blank' rel='noreferrer' className='break-all font-medium underline'>
                Open GitHub Actions workflow
              </a>
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
          {state === 'form' && (
            <>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleContinue}
                className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700'
              >
                Continue
              </button>
            </>
          )}
          {state === 'review' && (
            <>
              <button
                type='button'
                onClick={() => setState('form')}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
              >
                Back
              </button>
              <button
                type='button'
                onClick={() => setState('confirm')}
                className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700'
              >
                {preflight?.databaseRollbackStatus === 'unavailable' ? 'Continue code-only' : 'Continue'}
              </button>
            </>
          )}
          {state === 'confirm' && (
            <>
              <button
                type='button'
                onClick={() => setState(jobServiceName.trim() === '' ? 'review' : 'form')}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
              >
                Back
              </button>
              <button
                type='button'
                onClick={() => void handleDispatch()}
                className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700'
              >
                Confirm rollback
              </button>
            </>
          )}
          {state === 'success' && (
            <button
              type='button'
              onClick={onClose}
              className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
